import React, { ChangeEvent, useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import DashboardCreateSelectNetworkModal from "../modal/SelectNetworks";
import { Device, NetworkSelected, NetworkType } from "types";
import SelectedList, { TableColumn } from "./SelectedList";
import Icon from "@mui/material/Icon";
import Typography from "@mui/material/Typography";
import {
    CHART_ERROR_MESSAGE,
    CHART_NAME_PATTERN,
    ColorPicker,
    CreateChartState,
    useCreateDashboardStore,
} from "..";
import { ChartSource, ChartType } from "constant/chart";
import { DASHBOARD_CHART_MAX } from "constant";
import { EditWidgetProps } from ".";
import { BreakLine } from "components/common";
import { getMQTTQueryByDevice } from "components/dashboard/utils/widgetUtil";
import { getWidgetConfigByWidgetType, WidgetType } from "components/dashboard/utils/widgetConfig";

type CreateDataType = {
    widgetName: string;
    attributes: NetworkSelected[];
};

const CreateData: CreateChartState = {
    title: "Creating New Widget(s)",
    data: [],
    columns: [
        {
            key: "widgetName",
            name: "Widget Name",
        },
        {
            key: "count",
            name: "No. of Rooms",
            render: (data: any) => <span>{data.attributes.length}</span>,
        },
    ],
    transform: (data: CreateDataType) => ({
        attributes: data.attributes.map((a) => {
            return {
                color: a.color,
                device_id: a.ldsuUID,
                gateway_id: a.gatewayId,
                said: 0,
                name: a.deviceName,
                time_series: getMQTTQueryByDevice(a),
            }
        }),
        source: ChartSource.AGGREGATED_MULTIPLE_SENSORS,
        name: data.widgetName,
        chart: ChartType.BAR,
    }),
    validate() {
        return this.data.map((item) => ({
            path: `[name="${item.id}.widgetName"]`,
            isValid: CHART_NAME_PATTERN.test((item.widgetName ?? "").trim()),
            message: CHART_ERROR_MESSAGE,
        }));
    },
};

const Columns: TableColumn<NetworkSelected>[] = [
    { key: "deviceName", label: "Room Name", className: "text-truncate col-2" },
    { key: "gatewayName", label: "Network Name" },
    { key: "gatewayId", label: "Network ID", className: "text-truncate" },
    {
        key: "actions",
        label: "",
        render: (item, props) => (
            <span className="d-flex justify-content-end">
                <Icon
                    role="button"
                    aria-label="remove-icon"
                    className="cursor-pointer mr-2 text-primary-red-1"
                    onClick={() => props.onRemove(item)}
                >
                    close
                </Icon>
            </span>
        ),
        className: "col-1 text-end",
    },
];

const DefaultColor = "#0c74d5";

const CreateMultipleUsageWidget = ({
    onChange,
    panel,
}: EditWidgetProps) => {
    const { widget } = useCreateDashboardStore();
    const widgetType = widget?.widgetType ?? WidgetType.PSL_CHART_MULTIPLE_ROOM;
    const widgetConfiguration = getWidgetConfigByWidgetType(widgetType);
    const defaultWidgetName =
        widgetConfiguration.defaultWidgetName || "Accumulated Room Usage";
    const [showSelectModal, setShowSelectModal] = useState(false);
    const [widgetName, setWidgetName] = useState(panel?.name ?? defaultWidgetName);
    const [color, setColor] = useState<string>(DefaultColor);
    const [selectedNetworks, setSelectedNetworks] = useState<
        Record<string, NetworkSelected>
    >({});
    const { deviceList } = useCreateDashboardStore();
    const { attributes } = panel ?? {};
    useEffect(() => {
        if (!attributes) {
            return;
        }
        const selected: { [key: string]: NetworkSelected } = {};
        attributes.forEach((attribute: any) => {
            const network = deviceList.find(n => n.deviceId === `${attribute.device_id}/${attribute.said}` && n.gatewayId === attribute.gateway_id);
            if (network) {
                selected[`${attribute.device_id}/${attribute.said}`] = {
                    ...network,
                    color: attribute.color,
                    time_series: getMQTTQueryByDevice(network),
                };
            }

        });
        setSelectedNetworks(selected);

        // Set color from first attribute in update mode
        if (attributes.length > 0 && attributes[0].color) {
            setColor(attributes[0].color);
        }
    }, [attributes, deviceList]);

    useEffect(() => {
        useCreateDashboardStore.setState({
            createData: {
                ...CreateData,
                data: [
                    {
                        widgetName,
                        attributes: Object.values(selectedNetworks),
                    },
                ]
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [color, widgetName, selectedNetworks, widgetConfiguration]);

    const networks = Object.values(selectedNetworks);
    const isEdit = !!panel?.uuid;

    const onSelectNetwork = (networks: Record<string, Device>) => {
        const selected: Record<string, NetworkSelected> = Object.values(
            networks
        )
            .map((item: NetworkSelected, index: number) => ({
                ...item,
                widgetName: item.deviceName,
                color: color,
                time_series: getMQTTQueryByDevice(item),
            }))
            .pluck("deviceId");
        setSelectedNetworks(selected);

        onChange?.({
            chartName: widgetName,
            data: Object.values(selected).map((item) => ({
                ...item,
                color: color,
            })),
        });
    };

    const onItemRemove = (network: NetworkSelected) => {
        const selected = { ...selectedNetworks };
        delete selected[network.deviceId];
        setSelectedNetworks(selected);
        onChange?.({
            chartName: widgetName,
            data: Object.values(selected).map((item) => ({
                ...item,
                color: color,
            })),
        });
    };

    const onColorChange = (color: string) => {
        setColor(color);

        // Update color in all selected networks
        const updatedNetworks = Object.fromEntries(
            Object.entries(selectedNetworks).map(([key, network]) => [
                key,
                { ...network, color: color }
            ])
        );
        setSelectedNetworks(updatedNetworks);
        onChange?.({
            chartName: widgetName,
            data: Object.values(updatedNetworks).map((item) => ({
                ...item,
                color: color,
            })),
        });
    };

    const onNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        const name = e.target.value;
        setWidgetName(e.target.value)
        onChange?.({
            chartName: name,
            data: networks.map((item) => ({
                ...item,
                color: color,
            })),
        });
    };

    const filterDevices = (device: Device) => {
        return device.deviceType === NetworkType.ROOM;
    }


    return (
        <>
            <Row>
                <Col sm="12">
                    <Form.Group>
                        <Form.Label htmlFor="widget-name">
                            Widget Name
                        </Form.Label>
                        <Form.Control
                            id="widget-name"
                            name="widget-name"
                            type="text"
                            value={widgetName}
                            placeholder="Widget Name"
                            onChange={onNameChange}
                        />
                    </Form.Group>
                    <BreakLine
                        className={`${isEdit ? "my-4" : "my-3"}`}
                    />
                    <Typography variant="body1">Colors</Typography>
                    <div className="d-flex align-items-center my-2">
                        <Form.Group className="mr-2">
                            <ColorPicker
                                aria-label="color-picker"
                                defaultValue={color}
                                onChange={(e, value) => onColorChange(value)}
                            />
                        </Form.Group>
                    </div>
                    <BreakLine
                        className={`${isEdit ? "my-4" : "my-3"}`}
                    />
                    <div className="d-flex align-items-center gap-3">
                        <Button
                            variant="primary"
                            className="mr-2"
                            onClick={() => setShowSelectModal(true)}
                        >
                            Select Rooms
                        </Button>
                        {!!widgetConfiguration.maxItems && (
                            <span className="reusable-description" style={{ fontSize: 14, color: "#828FAB" }}>
                                (Max {widgetConfiguration.maxItems} rooms)
                            </span>
                        )}
                    </div>
                    <DashboardCreateSelectNetworkModal
                        show={showSelectModal}
                        maxSelected={widgetConfiguration.maxItems ?? DASHBOARD_CHART_MAX}
                        onHide={() => setShowSelectModal(false)}
                        selected={selectedNetworks}
                        onSave={(event, data) => {
                            onSelectNetwork(data);
                        }}
                        filter={filterDevices}
                        networkType={NetworkType.ROOM}
                    />
                </Col>
            </Row>
            <Row className="mt-4">
                {networks.length > 0 && (
                    <SelectedList
                        data={networks}
                        onRemove={onItemRemove}
                        columns={Columns}
                    />
                )}
            </Row>
        </>
    );
};

export default React.memo(CreateMultipleUsageWidget);